// src/pages/SalesPharma/salesSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCustomerList,
  fetchSalesVoucherNumber,
  createSales,
  fetchSpecialPrices,
  fetchDiscountAccounts,
  fetchBatchesForItem,
  fetchPackingStructureForItem,
  fetchPatients,
  fetchDoctors,
  fetchInsuranceCompanies,
  editPharmaSales,
  fetchAvailableTatmeenSerials
} from "./salesThunks";
import { currentDateTime } from "../../lib/functions";

const initialState = {

  entryDate: currentDateTime(),
  item_description: "",
  showDescription: false,
  customersList: null,
  selectedCustomer: null,
  salesVoucherNumber: null,
  defaultAccount: null,
  salesSuccess: null,
  discountAccount: null,
  specialPrices: [],
  tag_field_id: [],
  loading: false,
  error: null,
  // Items in the cart
  itemCart: [],

  // Payment service rows (transport / service)
  payServiceCart: [],

  // Footer totals
  footerValues: {
    subtotal: 0,
    total_discount: 0,
    total_tax: 0,
    grandTotal: 0,
    payServiceCart: [],
  },

  // Additional mapped customerInfo (quotation → sales)
  customerInfo: null,

  // -------------------------------------------------------
  // BATCHES
  // -------------------------------------------------------
  batchesList: [],
  loadingBatches: false,
  errorBatches: null,

  // -------------------------------------------------------
  // PACKING STRUCTURE
  // -------------------------------------------------------
  packingStructure: [],
  loadingPacking: false,
  errorPacking: null,

  // -------------------------------------------------------
  // PATIENT / DOCTOR / INSURANCE MASTER
  // -------------------------------------------------------
  patientsList: [],
  doctorsList: [],
  insuranceCompanies: [],

  // -------------------------------------------------------
  // PATIENT FLOW FIELDS (FLAT STATE)
  // -------------------------------------------------------
  patientCopayPercent: 0,
  insuranceCompanyId: null,
  selectedDoctorId: null,
  showDoctorField: false,
  deliveryType: null,
  patientDueAmount: 0,
  insuranceDueAmount: 0,
  isTatmeenProduct: false,
  ssccCode: "",
  isTatmeenRequired: false,
};

const salesSlice = createSlice({
  name: "salesPharma",
  initialState,
  reducers: {
    setSelectedCustomer(state, action) {
      state.selectedCustomer = action.payload;
    },
    setEntryDate(state, action) {
      state.entryDate = action.payload;
    },
    resetEntryDate(state, action) {
      state.entryDate = currentDateTime();
    },

    setResetSales(state) {
      state.salesSuccess = null;
      state.error = null;
    },
    setIsTatmeenRequired(state, action) {
      state.isTatmeenRequired = action.payload;
    },
    setPatientDueAmount(state, action) {
      state.patientDueAmount = Number(action.payload || 0);
    },
    setInsuranceDueAmount(state, action) {
      state.insuranceDueAmount = Number(action.payload || 0);
    },

    // Tag
    setTagFeildNameid(state, action) {
      state.tag_field_id = action.payload;
    },

    // ---------------------------------------------------
    // PATIENT FLOW REDUCERS
    // ---------------------------------------------------
    setPatientCopayPercent(state, action) {
      state.patientCopayPercent = Number(action.payload || 0);
    },

    setInsuranceCompanyId(state, action) {
      state.insuranceCompanyId = action.payload || null;
    },

    setSelectedDoctorId(state, action) {
      state.selectedDoctorId = action.payload || null;
    },

    setDeliveryType(state, action) {
      state.deliveryType = action.payload;
    },

    setShowDoctorField(state, action) {
      state.showDoctorField = !!action.payload;
    },

    setIsTatmeenProduct(state, action) {
      state.isTatmeenProduct = action.payload;
    },

    setSsccCode(state, action) {
      state.ssccCode = action.payload;
    },
    // Recalculate patient amounts based on grandTotal
    recalcPatientAmounts(state, action) {
      const grandTotal = Number(action.payload?.grandTotal || 0);
      const copay = Number(state.patientCopayPercent || 0);

      if (state.insuranceCompanyId) {
        const patientAmt = grandTotal - (grandTotal * copay) / 100;
        const insuranceAmt = (grandTotal * copay) / 100;

        state.patientDueAmount = Number(patientAmt.toFixed(2));
        state.insuranceDueAmount = Number(insuranceAmt.toFixed(2));
      } else {
        state.patientDueAmount = Number(grandTotal.toFixed(2));
        state.insuranceDueAmount = 0;
      }
    },

    resetPatientFlow(state) {
      state.patientCopayPercent = 0;
      state.insuranceCompanyId = null;
      state.selectedDoctorId = null;
      state.showDoctorField = false;
      state.patientDueAmount = 0;
      state.insuranceDueAmount = 0;
    },

    // ---------------------------------------------------
    // LOAD QUOTATION → SALES
    // ---------------------------------------------------
    loadQuotationForSales(state, action) {
      const q = action.payload;

      state.customerInfo = {
        acc_id: q.acc_id,
        acc_name: q.acc_name,
        address: q.address,
        contact_no: q.contact_no,
      };

      state.itemCart =
        q.details?.map((d, index) => ({
          key: index + 1,
          item_id: d.item_id,
          item_name: d.item_name,
          quantity: d.item_qty,
          rate: d.item_rate,
          discount: d.item_discount_per,
          tax_per: d.item_tax_per,
          total: d.item_total,
        })) || [];

      state.footerValues = {
        ...state.footerValues,
        subtotal: q.sub_total,
        total_discount: q.total_discount,
        total_tax: q.total_tax,
        grandTotal: q.total_amount,
        payServiceCart: q.otherServiceTrans || [],
      };

      state.payServiceCart = q.otherServiceTrans || [];
    },
  },

  // -------------------------------------------------------------
  // EXTRA REDUCERS
  // -------------------------------------------------------------
  extraReducers: (builder) => {
    builder

      // ---------------- Customers ----------------
      .addCase(fetchCustomerList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerList.fulfilled, (state, action) => {
        state.loading = false;
        state.customersList = action.payload;
      })
      .addCase(fetchCustomerList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---------------- Voucher Number ----------------
      .addCase(fetchSalesVoucherNumber.fulfilled, (state, action) => {
        state.salesVoucherNumber = action.payload.salesVoucherNumber;
        state.defaultAccount = action.payload.defaultAccount;
      })

      // ---------------- Create Sales ----------------
      .addCase(createSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSales.fulfilled, (state, action) => {
        state.loading = false;
        state.salesSuccess = action.payload;
      })
      .addCase(createSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---------------- Create Sales ----------------
      .addCase(editPharmaSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editPharmaSales.fulfilled, (state, action) => {
        state.loading = false;
        state.salesSuccess = action.payload;
      })
      .addCase(editPharmaSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---------------- Special Prices ----------------
      .addCase(fetchSpecialPrices.fulfilled, (state, action) => {
        state.specialPrices = action.payload;
      })

      // ---------------- Discount Account ----------------
      .addCase(fetchDiscountAccounts.fulfilled, (state, action) => {
        state.discountAccount = action.payload;
      })

      // ---------------- Batches ----------------
      .addCase(fetchBatchesForItem.pending, (state) => {
        state.loadingBatches = true;
      })
      .addCase(fetchBatchesForItem.fulfilled, (state, action) => {
        state.loadingBatches = false;
        state.batchesList = action.payload || [];
      })

      // ---------------- Packing ----------------
      .addCase(fetchPackingStructureForItem.pending, (state) => {
        state.loadingPacking = true;
      })
      .addCase(fetchPackingStructureForItem.fulfilled, (state, action) => {
        state.loadingPacking = false;
        state.packingStructure = action.payload || [];
      })

      // ---------------- Patient / Doctor / Insurance ----------------
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.patientsList = action.payload || [];
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
        state.doctorsList = action.payload || [];
      })
      .addCase(fetchInsuranceCompanies.fulfilled, (state, action) => {
        state.insuranceCompanies = action.payload || [];
      });
  },
});

export const {
  setSelectedCustomer,
  setResetSales,
  setTagFeildNameid,
  setPatientCopayPercent,
  setInsuranceCompanyId,
  setSelectedDoctorId,
  setShowDoctorField,
  recalcPatientAmounts,
  resetPatientFlow,
  loadQuotationForSales,
  setDeliveryType,
  setEntryDate,
  setInsuranceDueAmount,
  setPatientDueAmount,
  resetEntryDate,
  setIsTatmeenProduct,
  setSsccCode,
  setIsTatmeenRequired
} = salesSlice.actions;

export default salesSlice.reducer;
